import * as ko from "knockout";
import { Modal } from "bootstrap";
import { RcmdArtist } from "static/classes/Artist";
import type {
    Lang,
    Localized,
    Section,
    LocalizedTemplate,
    RcmdTargetEvent,
    RootVm,
    EmailTemplateVM,
} from "../types";

declare const $: any;
declare const notyf: any;

type Variant = "normal" | "recommendation";

type EventTypeKey =
    | "sofaconcert"
    | "birthday"
    | "corporate_event"
    | "special_occasion"
    | "livestream"
    | "wedding"
    | "other";

const PRETTY_EVENT_TYPE: Record<Lang, Record<EventTypeKey, string>> = {
    de: {
        sofaconcert: "dein Sofakonzert",
        birthday: "deinen Geburtstag",
        corporate_event: "deine Firmenfeier",
        special_occasion: "deinen besonderen Anlass",
        livestream: "dein Livestream-Konzert",
        wedding: "deine Hochzeit",
        other: "dein Event",
    },
    en: {
        sofaconcert: "sofaconcert",
        birthday: "birthday",
        corporate_event: "corporate_event",
        special_occasion: "special_occasion",
        livestream: "special_occasion",
        wedding: "wedding",
        other: "other",
    },
    fr: {
        sofaconcert: "sofaconcert",
        birthday: "birthday",
        corporate_event: "corporate_event",
        special_occasion: "special_occasion",
        livestream: "special_occasion",
        wedding: "wedding",
        other: "other",
    },
};

export type RecommendationsParams = {
    rootvm: RootVm;
    sectionCount?: number;
};

export class RecommendationsTemplateVM implements EmailTemplateVM {
    type = "RecommendationsTemplate";
    rootvm: RootVm;

    language = ko.observable<Lang>("de");

    subject = ko.observable<string>("");
    hi = ko.observable<string>("");
    greetings = ko.observable<string>("");
    subject_template = ko.observable<Localized>({ de: "", en: "", fr: "" });
    hi_template = ko.observable<Localized>({ de: "", en: "", fr: "" });
    greetings_template = ko.observable<Localized>({ de: "", en: "", fr: "" });

    sections: ko.ObservableArray<Section>;

    host_name = ko.observable<string>("");
    event = ko.observable<RcmdTargetEvent | undefined>(undefined);
    event_type = ko.observable<string>("");
    display_name = ko.observable<string>("");
    date = ko.observable<string | undefined>(undefined);
    person_uuid = ko.observable<string>("");
    recommendation_uuid = ko.observable<string>("");

    pretty_event_date: ko.PureComputed<string>;
    pretty_event_type: ko.PureComputed<string>;

    all_artists = ko.observableArray<RcmdArtist>([]);
    recommend_artists = ko.observableArray<RcmdArtist>([]);
    selectedArtists = ko.observableArray<RcmdArtist>([]);
    searchTerm = ko.observable<string>("").extend({ rateLimit: 500 });
    artist_uuids = ko.observable<string>("");
    filteredArtists: ko.Computed<RcmdArtist[]>;

    ai_prepopulate_loading = ko.observable<boolean>(false);
    ai_prepopulate_error = ko.observable<string>("");
    ai_prepopulate_reasoning = ko.observable<string>("");
    ai_extra_instructions = ko.observable<string>("");

    set_default_for = ko.observable<string | undefined>(undefined);
    set_default: ko.PureComputed<{ artist_name: any; recommendation_text: any; artist_uuid?: any }>;

    setDefaultModalId = "emailComposerSetDefaultModal";

    constructor(params: RecommendationsParams) {
        this.rootvm = params.rootvm;

        const sectionCount = params.sectionCount ?? 2;
        this.sections = ko.observableArray<Section>(
            Array.from({ length: sectionCount }, (_, i) => this.makeSection(i))
        );

        this.pretty_event_date = ko.pureComputed(() => {
            const lang = this.language();
            if (this.date()) {
                return new Date(this.date() as string).toLocaleDateString(lang);
            }
            const ev = this.event();
            const dates = ev?.possible_dates?.();
            if (dates && dates.length > 0) {
                return new Date(dates[0]).toLocaleDateString(lang);
            }
            return "";
        });

        this.pretty_event_type = ko.pureComputed(() => {
            const t = (this.event_type() || this.event()?.event_type?.() || "") as EventTypeKey;
            if (!t) return "";
            return PRETTY_EVENT_TYPE[this.language()]?.[t] ?? t;
        });

        this.filteredArtists = ko.computed(() => {
            const term = this.searchTerm();
            if (!term) return [];
            const lower = term.toLowerCase();
            return this.all_artists().filter((a) =>
                (a.artist_name() ?? "").toLowerCase().includes(lower)
            );
        });

        this.set_default = ko.pureComputed(() => {
            const target = this.set_default_for();
            const artist = this.recommend_artists().find((a) => a.artist_uuid() === target);
            return artist ?? { artist_name: "", recommendation_text: "" };
        });

        this.language.subscribe((lang) => {
            if (!lang) return;
            const variant: Variant = this.recommendation_uuid() ? "recommendation" : "normal";
            this.populate(variant, lang);
        });
    }

    private makeSection(index: number): Section {
        return {
            label: `Section ${index + 1}`,
            value: ko.observable<string>(""),
            template: ko.observable<Localized>({ de: "", en: "", fr: "" }),
            rows: 10,
        };
    }

    private buildTemplate(variant: Variant): LocalizedTemplate {
        const date = this.pretty_event_date();
        const host = this.host_name();
        const display = this.display_name();

        const hi: Localized = {
            de: `Hallo ${host},`,
            en: `Hi ${host},`,
            fr: `Bonjour ${host},`,
        };
        const greetings: Localized = {
            de: `Liebe Grüße, ${display} vom SofaConcerts Team`,
            en: `Best regards, ${display} from the SofaConcerts Team`,
            fr: `Cordialement, ${display} de l'équipe SofaConcerts`,
        };
        const closingSection: Localized = {
            de: `Ich habe für dich alle Künstler*innen auch nochmal auf dieser <a href="{fav_link}">persönlichen Merkliste</a> zusammengefasst.\n\nWenn ich noch weiterhelfen kann mit alternativen Empfehlungen oder Du Unterstützung bei den Anfragen benötigst, melde Dich immer sehr gerne bei mir, per Mail an <a href="mailto:support@sofaconcerts.org">support@sofaconcerts.org</a> oder telefonisch unter 040 88177-500.\n\nIch bin mir sicher, wir finden den perfekten Act für Dein Event.`,
            en: `I've also saved all the artists on your <a href="{fav_link}">personal favourites list</a> for you.\n\nIf you need any help with alternative recommendations or support with your enquiries, don't hesitate to get in touch — by email at <a href="mailto:support@sofaconcerts.org">support@sofaconcerts.org</a> or by phone on +49 40 88177-500.\n\nI'm sure we'll find the perfect act for your event.`,
            fr: `J'ai également rassemblé tous les artistes sur votre <a href="{fav_link}">liste de favoris personnelle</a>.\n\nSi vous avez besoin d'aide avec des recommandations alternatives ou d'un soutien pour vos demandes, n'hésitez pas à me contacter par e-mail à <a href="mailto:support@sofaconcerts.org">support@sofaconcerts.org</a> ou par téléphone au +49 40 88177-500.\n\nJe suis sûr(e) que nous trouverons l'artiste parfait pour votre événement.`,
        };

        if (variant === "recommendation") {
            return {
                subject: {
                    de: `Re: Deine SofaConcerts Anfrage | Event am ${date}`,
                    en: `RE: Sofaconcerts Recommendations for your event on ${date}`,
                    fr: `RE: Recommandations SofaConcerts pour votre événement du ${date}`,
                },
                hi,
                sections: [
                    {
                        de: `Danke für Deine Anfrage. Wir haben uns für Dich auf die Suche gemacht und spannende Acts gefunden, die dir gefallen könnten.\n\nIch habe für Dich alle Vorschläge auf einer persönlichen Merkliste zusammengefasst. Dort kannst Du sie in Ruhe anschauen und vor allem anhören. Als kleinen Vorgeschmack habe ich Dir die Auswahl hier nochmal kurz zusammengefasst und Dir weitere Infos zum Artist gegeben – eine Art Kurzvorstellung. Viel Spaß beim Durchklicken und Hören.\n\nKleiner Tipp noch: schreibe am besten 2-3 Musiker oder Musikerinnen an, um die Chance zu erhöhen, dass es klappt. Manche Acts haben bereits einen Gig, daher lohnt es sich immer, direkt mehrere anzufragen.`,
                        en: `Thanks for your enquiry. We've been searching for you and found some exciting acts that we think you'll love.\n\nI've put together all the suggestions on a personal favourites list for you. There you can browse through them at your leisure and, most importantly, have a listen. As a little taster, I've also summarised the selection below with some extra info about each artist — a quick introduction. Enjoy clicking through and listening!\n\nA quick tip: it's best to reach out to 2–3 artists to increase your chances of finding the right match. Some acts may already have a booking, so it's always worth contacting several at once.`,
                        fr: `Merci pour votre demande. Nous avons cherché pour vous et trouvé des artistes passionnants qui pourraient vous plaire.\n\nJ'ai rassemblé toutes les suggestions sur une liste de favoris personnelle. Vous pouvez y jeter un œil tranquillement et surtout les écouter. En avant-première, j'ai aussi résumé la sélection ci-dessous avec quelques infos supplémentaires sur chaque artiste. Bonne écoute !\n\nPetit conseil : contactez de préférence 2 à 3 artistes pour augmenter vos chances. Certains ont parfois déjà un engagement, il vaut donc mieux en contacter plusieurs directement.`,
                    },
                    {
                        de: `Ich habe für dich alle Künstler*innen auch nochmal auf dieser <a href="{fav_link}">persönlichen Merkliste</a> zusammengefasst.\n\nWenn ich noch weiterhelfen kann mit alternativen Empfehlungen oder Du Unterstützung bei den Anfragen benötigst, melde Dich immer sehr gerne bei mir, per Mail an <a href="mailto:support@sofaconcerts.org">support@sofaconcerts.org</a> oder telefonisch unter 040 88177-500.\n\nIch bin mir sicher, wir finden den perfekten Act für Dein Event. Jetzt bist aber erstmal Du gefragt: schreibe am besten direkt mehrere Acts an. On top, kannst Du auf SofaConcerts auch nochmal weitere Acts anschauen und anschreiben. Melde dich gerne jederzeit, wenn du Unterstützung benötigst.`,
                        en: `I've also saved all the artists on your <a href="{fav_link}">personal favourites list</a>.\n\nIf you need any help with alternative recommendations or support with your enquiries, don't hesitate to get in touch — by email at <a href="mailto:support@sofaconcerts.org">support@sofaconcerts.org</a> or by phone on +49 40 88177-500.\n\nI'm sure we'll find the perfect act for your event. But now it's over to you — it's best to reach out to several acts straight away. You can also browse and contact more artists on SofaConcerts. Feel free to get in touch any time if you need support.`,
                        fr: `J'ai également rassemblé tous les artistes sur votre <a href="{fav_link}">liste de favoris personnelle</a>.\n\nSi vous avez besoin d'aide avec des recommandations alternatives ou d'un soutien pour vos demandes, n'hésitez pas à me contacter par e-mail à <a href="mailto:support@sofaconcerts.org">support@sofaconcerts.org</a> ou par téléphone au +49 40 88177-500.\n\nJe suis sûr(e) que nous trouverons l'artiste parfait pour votre événement. Mais c'est maintenant à vous de jouer : contactez de préférence plusieurs artistes directement. Vous pouvez également parcourir et contacter d'autres artistes sur SofaConcerts. N'hésitez pas à nous contacter à tout moment si vous avez besoin d'aide.`,
                    },
                ],
                greetings,
            };
        }

        return {
            subject: {
                de: `Re: Deine SofaConcerts Anfrage für Dein Event am ${date}`,
                en: `Re: Your SofaConcerts enquiry for your event on ${date}`,
                fr: `Re: Votre demande SofaConcerts pour votre événement du ${date}`,
            },
            hi,
            sections: [
                {
                    de: `vielen Dank für die Anfrage für Dein Event am ${date}.\n\nIch habe mich für Dich nach passenden Acts umgesehen und sende Dir hier ein paar Empfehlungen zu, welche ich mir sehr gut vorstellen könnte. Hör Dich gerne einfach mal durch die verschiedenen Soundbeispiele auf den Profilen durch.\n\nDeine Favorit*innen kannst Du direkt über unsere Website anfragen und mit ihnen in Kontakt treten:`,
                    en: `Thank you for your enquiry for your event on ${date}.\n\nI've been looking for the right acts for you and have put together a few recommendations that I think would be a great fit. Feel free to listen through the sound samples on their profiles.\n\nYou can contact your favourites directly through our website:`,
                    fr: `Merci pour votre demande pour votre événement du ${date}.\n\nJ'ai cherché des artistes qui pourraient vous convenir et je vous envoie quelques recommandations qui me semblent très adaptées. N'hésitez pas à écouter les extraits sonores sur leurs profils.\n\nVous pouvez contacter vos favoris directement via notre site :`,
                },
                closingSection,
            ],
            greetings,
        };
    }

    populate = (variant: Variant, language: Lang = this.language()) => {
        const tpl = this.buildTemplate(variant);

        this.subject_template(tpl.subject);
        this.hi_template(tpl.hi);
        this.greetings_template(tpl.greetings);

        tpl.sections.forEach((localized, i) => {
            const section = this.sections()[i];
            if (!section) return;
            section.template(localized);
            section.value(localized[language] ?? "");
        });

        this.subject(tpl.subject[language] ?? "");
        this.hi(tpl.hi[language] ?? "");
        this.greetings(tpl.greetings[language] ?? "");
    };

    lookup_all_artists = async () => {
        const response = await fetch("/api/admin/artists/all-active?min_rank=0");
        const result = await response.json();
        if (result.success === true) {
            this.all_artists(result.artists.map((a: any) => new RcmdArtist(a)));
            return this.all_artists();
        }
        return [];
    };

    sort_all_artists = () => {
        this.all_artists.sort((y: any, x: any) => {
            if (x.selected() === y.selected()) {
                return x.admin_ranking() >= y.admin_ranking() ? 1 : -1;
            }
            return Number(x.selected()) - Number(y.selected());
        });
    };

    isArtistSelected = (artist: RcmdArtist) =>
        this.selectedArtists().some((a) => a.artist_uuid() === artist.artist_uuid());

    addArtist = (artist: RcmdArtist) => {
        this.recommend_artists.push(artist);
        this.searchTerm("");
    };

    removeArtist = (artist: RcmdArtist) => {
        this.recommend_artists.remove(artist);
    };

    add_artist_uuids = () => {
        const raw = this.artist_uuids();
        if (!raw) return;
        try {
            const uuids: string[] = JSON.parse(raw);
            const artists = this.all_artists().filter(
                (a) => uuids.indexOf(a.artist_uuid() ?? "") >= 0
            );
            artists.forEach((a) => this.recommend_artists.push(a));
            this.artist_uuids("");
        } catch (e) {
            console.error("Invalid artist_uuids JSON", e);
        }
    };

    prepopulate_artist_matches = () => {
        // Two entry points share this button: a matchmaking recommendation
        // (recommendation_uuid, no event yet) and an existing event. Both run
        // the same AI pipeline - only the start endpoint/payload differs.
        const recommendation_uuid = this.recommendation_uuid();
        const event_uuid = this.event()?.event_uuid();

        if (!recommendation_uuid && !event_uuid) return;

        this.ai_prepopulate_loading(true);
        this.ai_prepopulate_error("");

        const url = recommendation_uuid
            ? "/api/admin/ai-recommendations/from-recommendation"
            : "/api/admin/ai-recommendations/start";

        // The composer's language selector defaults to the recipient's profile
        // language and can be overridden by the admin. Pass it through so the AI
        // writes the intro and per-artist blurbs in that language.
        const target_language = this.language() || null;

        const body = recommendation_uuid
            ? { recommendation_uuid, extra_instructions: this.ai_extra_instructions() || null, target_language }
            : { event_uuid, extra_instructions: this.ai_extra_instructions() || null, target_language };

        fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        })
            .then((r) => r.json())
            .then((data) => {
                if (!data.success) throw new Error(data.message || "Failed to start AI job");
                this.poll_ai_draft(data.draft_uuid);
            })
            .catch((err) => {
                this.ai_prepopulate_error("Error starting AI job: " + err.message);
                this.ai_prepopulate_loading(false);
            });
    };

    private apply_ai_result = (result: any) => {
        if (!result) return;

        if (result.email_intro && this.sections().length > 0) {
            this.sections()[0].value(result.email_intro);
        }

        if (result.reasoning) {
            this.ai_prepopulate_reasoning(result.reasoning);
        }

        this.recommend_artists([]);
        (result.recommendations || []).forEach((rec: any) => {
            const artist = this.all_artists().find(
                (a) => a.artist_uuid() === rec.artist_uuid
            );
            if (artist) {
                (artist as any).recommendation_text(rec.ai_description);
                this.recommend_artists.push(artist);
            }
        });

        if (result.warnings?.length) {
            console.warn("AI recommendation warnings:", result.warnings);
        }
    };

    private poll_ai_draft = (draft_uuid: string) => {
        const poll = () => {
            fetch(`/api/admin/ai-recommendations/status?draft_uuid=${draft_uuid}`)
                .then((r) => r.json())
                .then((data) => {
                    if (!data.success) {
                        this.ai_prepopulate_error("Poll error: " + (data.message || ""));
                        this.ai_prepopulate_loading(false);
                        return;
                    }
                    if (data.status === "pending" || data.status === "running") {
                        setTimeout(poll, 2000);
                        return;
                    }
                    this.ai_prepopulate_loading(false);
                    if (data.status === "error") {
                        this.ai_prepopulate_error("AI job failed. Check logs.");
                        return;
                    }
                    this.apply_ai_result(data.result);
                })
                .catch((err) => {
                    this.ai_prepopulate_error("Poll error: " + err.message);
                    this.ai_prepopulate_loading(false);
                });
        };
        poll();
    };

    send_mail = () => {
        const data = {
            subject: this.subject(),
            hi: this.hi(),
            sections: this.sections().map((s) => s.value()),
            greetings: this.greetings(),
            rcmd_artists: ko.toJSON(this.recommend_artists()),
            event_uuid: this.event()?.event_uuid(),
            person_uuid: this.person_uuid(),
            recommendation_uuid: this.recommendation_uuid(),
            locale: this.language(),
        };
        return fetch("/api/admin/host/send-new-recommendations", {
            method: "POST",
            body: JSON.stringify(data),
        })
            .then((r) => r.json())
            .then(() => {
                notyf.success("Mail sent.");
                const ev = this.event();
                if (ev) {
                    ev.rcmd_artists_sent_on?.(new Date());
                    ev.rcmd_artists_sent_by?.({ name: this.display_name() });
                    ev.rcmd_artists?.(this.recommend_artists());
                    this.recommend_artists([]);
                }
            });
    };

    // Stack the set-default modal on top of the composer modal (like the
    // details-modal → quick-message flow) instead of data-bs-toggle, which
    // would hide the composer modal underneath.
    open_set_default = (artist_uuid: string) => {
        this.set_default_for(artist_uuid);
        const el = document.getElementById(this.setDefaultModalId);
        if (el) Modal.getOrCreateInstance(el).show();
    };

    save_default_text = () => {
        const target = this.set_default();
        const artist_uuid = ko.isObservable(target.artist_uuid)
            ? target.artist_uuid()
            : target.artist_uuid;
        const recommendation_text = ko.isObservable(target.recommendation_text)
            ? target.recommendation_text()
            : target.recommendation_text;

        return fetch(this.rootvm.web_host + "/api/admin/artist/recommendation-text", {
            method: "POST",
            body: JSON.stringify({ artist_uuid, recommendation_text }),
        })
            .then((r) => r.json())
            .then((response) => {
                if (response.success) {
                    // Reflect the persisted value locally so the card/modal show
                    // the new default without a page refresh.
                    if (ko.isObservable(target.recommendation_text)) {
                        target.recommendation_text(
                            response.recommendation_text ?? recommendation_text
                        );
                    }
                    notyf.success("Saved!");
                } else {
                    notyf.error("Error!");
                }
            });
    };
}
